import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const API = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [expiresIn, setExpiresIn] = useState(null);
  
  // Activity tracking refs
  const lastActivityRef = useRef(Date.now());
  const inactivityTimerRef = useRef(null);
  const tokenRefreshTimerRef = useRef(null);

  // Update last activity on any user interaction
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  // Setup activity listeners
  useEffect(() => {
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      window.addEventListener(event, updateActivity, { passive: true });
    });
    
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
    };
  }, [updateActivity]);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('tokenExpiry');
    // Clear PIN verification to force re-entry
    sessionStorage.removeItem('pin_verified');
    setToken(null);
    setUser(null);
    setExpiresIn(null);
    
    // Clear timers
    if (inactivityTimerRef.current) {
      clearInterval(inactivityTimerRef.current);
    }
    if (tokenRefreshTimerRef.current) {
      clearTimeout(tokenRefreshTimerRef.current);
    }
    
    // Redirect to PIN page
    window.location.href = '/pin';
  }, []);

  // Check inactivity and refresh token
  const checkInactivityAndRefresh = useCallback(async () => {
    if (!token || !user) return;
    
    const now = Date.now();
    const lastActivity = lastActivityRef.current;
    const inactiveTime = now - lastActivity;
    
    // Get inactivity limit based on role (in milliseconds)
    const isAdmin = ['super_admin', 'admin_ops', 'bureau', 'analyst'].includes(user.role);
    const inactivityLimit = isAdmin ? 48 * 60 * 60 * 1000 : 30 * 60 * 1000; // 48h for admin, 30min for client
    
    if (inactiveTime >= inactivityLimit) {
      console.log('Session expired due to inactivity');
      logout();
      return;
    }
    
    // Show warning 2 minutes before logout (only for clients)
    const warningThreshold = inactivityLimit - 2 * 60 * 1000;
    if (!isAdmin && inactiveTime >= warningThreshold && !window._inactivityWarningShown) {
      window._inactivityWarningShown = true;
      const remainSec = Math.round((inactivityLimit - inactiveTime) / 1000);
      try {
        const { toast } = await import('sonner');
        toast.warning('Session bientot expiree', {
          description: `Vous serez deconnecte dans ${remainSec > 60 ? Math.ceil(remainSec / 60) + ' minute(s)' : remainSec + ' secondes'} pour inactivite. Bougez la souris pour rester connecte.`,
          duration: 30000,
        });
      } catch {}
    }
    if (inactiveTime < warningThreshold) {
      window._inactivityWarningShown = false;
    }
    
    // Refresh token if user was active in the last 5 minutes and token expires soon
    const tokenExpiry = localStorage.getItem('tokenExpiry');
    if (tokenExpiry) {
      const expiryTime = parseInt(tokenExpiry);
      const timeUntilExpiry = expiryTime - now;
      
      // Refresh if token expires in less than 10 minutes and user was active recently
      if (timeUntilExpiry < 10 * 60 * 1000 && inactiveTime < 5 * 60 * 1000) {
        try {
          const response = await axios.post(`${API}/api/auth/refresh-token`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          const newToken = response.data.access_token;
          const newExpiresIn = response.data.expires_in;
          const newExpiry = Date.now() + (newExpiresIn * 1000);
          
          localStorage.setItem('token', newToken);
          localStorage.setItem('tokenExpiry', newExpiry.toString());
          setToken(newToken);
          setExpiresIn(newExpiresIn);
          
          console.log('Token refreshed successfully');
        } catch (error) {
          console.error('Failed to refresh token:', error);
          // Don't logout on refresh error - let the token expire naturally
        }
      }
    }
  }, [token, user, logout]);

  // Setup inactivity checker interval
  useEffect(() => {
    if (token && user) {
      // Check every minute
      inactivityTimerRef.current = setInterval(checkInactivityAndRefresh, 30 * 1000);
      
      return () => {
        if (inactivityTimerRef.current) {
          clearInterval(inactivityTimerRef.current);
        }
      };
    }
  }, [token, user, checkInactivityAndRefresh]);

  const fetchUser = useCallback(async (authToken) => {
    // Skip fetching user if in mirror mode - we already have the mock client user
    const inMirrorMode = sessionStorage.getItem('mirrorMode') === 'true';
    if (inMirrorMode) {
      const savedMirrorClient = sessionStorage.getItem('mirrorClient');
      if (savedMirrorClient) {
        try {
          const mirrorClientData = JSON.parse(savedMirrorClient);
          const mirrorUser = {
            id: mirrorClientData.user_id || mirrorClientData.id,
            email: mirrorClientData.user?.email || mirrorClientData.email || 'client@mirror.mode',
            first_name: mirrorClientData.user?.first_name || mirrorClientData.first_name || 'Client',
            last_name: mirrorClientData.user?.last_name || mirrorClientData.last_name || '',
            role: 'client',
            status: 'active'
          };
          setUser(mirrorUser);
          setMirrorMode(true);
          setMirrorClient(mirrorClientData);
          if (mirrorClientData?.user_id || mirrorClientData?.id) {
            axios.defaults.headers.common['X-Mirror-Client-Id'] = mirrorClientData.user_id || mirrorClientData.id;
          }
          return mirrorUser;
        } catch (e) {
          // Invalid mirror client data, exit mirror mode
          sessionStorage.removeItem('mirrorMode');
          sessionStorage.removeItem('mirrorClient');
        }
      }
    }
    
    try {
      const response = await axios.get(`${API}/api/auth/me`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setUser(response.data);
      localStorage.setItem('user', JSON.stringify(response.data));
      return response.data;
    } catch (error) {
      // Logout on 401 (invalid token) or 403 (account blocked)
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.log('Token invalid or account blocked, logging out');
        logout();
      } else {
        // For other errors (network, 500, etc.), try to use cached user
        const cachedUser = localStorage.getItem('user');
        if (cachedUser) {
          try {
            const parsed = JSON.parse(cachedUser);
            setUser(parsed);
            return parsed;
          } catch (e) {
            // Invalid cached data
          }
        }
        console.error('Error fetching user:', error);
      }
      return null;
    }
  }, [logout]);

  useEffect(() => {
    const initAuth = async () => {
      // PROTECTION: If we have an originalToken but NO mirrorMode in sessionStorage,
      // it means we're on a non-mirror tab that was polluted. Restore admin token.
      const isMirrorTab = sessionStorage.getItem('mirrorMode') === 'true';
      const originalToken = localStorage.getItem('originalToken');
      if (originalToken && !isMirrorTab) {
        // Restore admin token
        localStorage.setItem('token', originalToken);
        localStorage.removeItem('originalToken');
      }

      const savedToken = localStorage.getItem('token');
      if (savedToken) {
        setToken(savedToken);
        await fetchUser(savedToken);
      }
      setLoading(false);
    };
    initAuth();
  }, [fetchUser]);

  // Auto-activate mirror mode — MOVED: see after enterMirrorMode declaration

  const login = async (email, password) => {
    localStorage.removeItem('pending_mirror');
    try {
      const response = await axios.post(`${API}/api/auth/login`, { email, password });
      const { access_token, user: userData, expires_in, office_permissions } = response.data;
      
      // Store office permissions with user data
      if (office_permissions) {
        userData.office_permissions = office_permissions;
      }
      
      // Calculate and store expiry time
      const expiryTime = Date.now() + (expires_in * 1000);
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('tokenExpiry', expiryTime.toString());
      
      setToken(access_token);
      setUser(userData);
      setExpiresIn(expires_in);
      lastActivityRef.current = Date.now();
      
      return { success: true, user: userData };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(`${API}/api/auth/register`, userData, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Registration failed' 
      };
    }
  };

  const updateUser = async (data) => {
    try {
      const response = await axios.put(`${API}/api/auth/profile`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const updatedUser = { ...user, ...response.data };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Update failed' 
      };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      await axios.post(`${API}/api/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Refresh user data after password change
      await fetchUser(token);
      return { success: true };
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(d => d.msg || d).join(', ') : 'Password change failed';
      return { 
        success: false, 
        error: msg
      };
    }
  };

  // Mirror mode state
  const [mirrorMode, setMirrorMode] = useState(false);
  const [mirrorClient, setMirrorClient] = useState(null);
  const [originalUser, setOriginalUser] = useState(null);
  const [originalToken, setOriginalToken] = useState(null);

  // Enter mirror mode - login as client
  const enterMirrorMode = async (clientId) => {
    try {
      // Save current admin state
      setOriginalUser(user);
      setOriginalToken(token);
      localStorage.setItem('originalUser', JSON.stringify(user));
      localStorage.setItem('originalToken', token);
      // Store the clientId to return to client detail page on exit
      sessionStorage.setItem('mirrorClientId', clientId);
      
      // Get client data
      const response = await axios.get(`${API}/api/clients/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const clientData = response.data;
      
      // Create a mock client user object for the UI
      const mockClientUser = {
        id: clientData.user_id,
        email: clientData.user?.email || 'client@mirror.mode',
        first_name: clientData.user?.first_name || 'Client',
        last_name: clientData.user?.last_name || '',
        role: 'client',
        status: 'active'
      };
      
      setUser(mockClientUser);
      setMirrorClient(clientData);
      axios.defaults.headers.common['X-Mirror-Client-Id'] = clientData.user_id;
      setMirrorMode(true);
      sessionStorage.setItem('mirrorMode', 'true');
      sessionStorage.setItem('mirrorClient', JSON.stringify(clientData));
      
      return { success: true };
    } catch (error) {
      console.error('Failed to enter mirror mode:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Failed to enter mirror mode' 
      };
    }
  };

  // Exit mirror mode - return to admin
  const exitMirrorMode = () => {
    const savedUser = localStorage.getItem('originalUser');
    const savedToken = localStorage.getItem('originalToken');
    
    // Restore admin state
    if (savedUser && savedToken) {
      setUser(JSON.parse(savedUser));
      setToken(savedToken);
    }
    
    setMirrorMode(false);
    setOriginalUser(null);
    setOriginalToken(null);
    setMirrorClient(null);
    delete axios.defaults.headers.common['X-Mirror-Client-Id'];
    
    // Clear localStorage (but don't clear mirrorClientId yet - caller reads it first)
    localStorage.removeItem('originalUser');
    localStorage.removeItem('originalToken');
    sessionStorage.removeItem('mirrorMode');
    sessionStorage.removeItem('mirrorClient');
    // Note: mirrorClientId is intentionally NOT removed here
    // It's read by the caller before navigation, then cleared after page load
  };

  // Check for mirror mode on init
  useEffect(() => {
    const savedMirrorMode = sessionStorage.getItem('mirrorMode');
    const savedMirrorClient = sessionStorage.getItem('mirrorClient');
    
    if (savedMirrorMode === 'true' && savedMirrorClient) {
      const mirrorClientData = JSON.parse(savedMirrorClient);
      const mirrorUser = {
        id: mirrorClientData.user_id || mirrorClientData.id,
        email: mirrorClientData.user?.email || mirrorClientData.email || 'client@mirror.mode',
        first_name: mirrorClientData.user?.first_name || mirrorClientData.first_name || 'Client',
        last_name: mirrorClientData.user?.last_name || mirrorClientData.last_name || '',
        role: 'client',
        status: 'active'
      };
      setMirrorMode(true);
      setUser(mirrorUser);
      setMirrorClient(mirrorClientData);
      const savedOriginalUser = localStorage.getItem('originalUser');
      setOriginalUser(savedOriginalUser ? JSON.parse(savedOriginalUser) : null);
      setOriginalToken(localStorage.getItem('originalToken'));
      if (mirrorClientData?.user_id || mirrorClientData?.id) {
        axios.defaults.headers.common['X-Mirror-Client-Id'] = mirrorClientData.user_id || mirrorClientData.id;
      }
    }
  }, []);

  useEffect(() => {
    if (mirrorMode && (mirrorClient?.user_id || mirrorClient?.id)) {
      axios.defaults.headers.common['X-Mirror-Client-Id'] = mirrorClient.user_id || mirrorClient.id;
    } else {
      delete axios.defaults.headers.common['X-Mirror-Client-Id'];
    }
  }, [mirrorMode, mirrorClient]);

  // Auto-activate mirror mode from admin client list (new tab)
  useEffect(() => {
    const raw = localStorage.getItem('pending_mirror');
    if (!raw || !token || !user || loading || mirrorMode) return;
    if (!['super_admin', 'admin_ops', 'bureau', 'analyst'].includes(user.role)) {
      localStorage.removeItem('pending_mirror');
      return;
    }
    try {
      const stored = JSON.parse(raw);
      if (Date.now() - (stored.ts || 0) > 10000) {
        localStorage.removeItem('pending_mirror');
        return;
      }
      localStorage.removeItem('pending_mirror');
      enterMirrorMode(stored.id).then(() => {
        window.location.href = '/client/dashboard';
      }).catch(() => {});
    } catch {
      localStorage.removeItem('pending_mirror');
    }
  }, [token, user, loading, mirrorMode, enterMirrorMode]);

  const setupPin = async (pin) => {
    try {
      await axios.post(`${API}/api/auth/setup-pin`, { pin: String(pin) }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchUser(token);
      return { success: true };
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(d => d.msg || d).join(', ') : 'PIN setup failed';
      return { success: false, error: msg };
    }
  };

  const value = {
    user,
    token,
    loading,
    expiresIn,
    login,
    logout,
    register,
    updateUser,
    changePassword,
    setupPin,
    fetchUser,
    isAuthenticated: !!token && !!user,
    isAdmin: user?.role && ['super_admin', 'admin_ops', 'bureau'].includes(user.role),
    isAnalyst: user?.role === 'analyst',
    isClient: user?.role === 'client',
    // Mirror mode
    mirrorMode,
    enterMirrorMode,
    exitMirrorMode,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
